// Card lifecycle reset. Pure. No React, no broadcasts. Owns the
// single function that wipes every per-instance state field a card
// accumulates while in play, so that when the card re-enters the
// realm (from cemetery / hand / atlas / banished / collection) it
// arrives in its catalog-default state.
//
// ── Sorcery rule (codex "Cemetery"/"Hand"/"Token", implicit) ──
// A card leaving the realm becomes a clean re-spawn point. Damage,
// status effects, stat buffs/debuffs, ownership claims, ward tokens,
// tap state, etc. are all transient to that one stay in the realm
// and do NOT persist across the leave. Re-playing a card from the
// cemetery summons a fresh copy with the printed stats.
//
// Why one function: every subsystem that mutates per-instance state
// (statuses, wards, control claims, aura deltas, manual stat edits)
// needs its mutations reverted on leave. Without a single reset hook
// each subsystem would have to remember to clean up after itself.
// historically that has been the source of "stuck +1/+1 buff on a
// replayed card" bugs.
//
// What is NOT reset (intrinsic to the printing / per-instance identity):
//   • id, cardId, name, type, isSite, isToken
//   • imageUrl, imagePath, printingId, foiling
//   • subTypes, _printedKeywords
//   • baseAttack, baseLife (catalog snapshot. CurrentAttack/Life
//     re-initialize from these in addCardToTable on re-entry)
//   • rotated. Controller. Reverted to _origRotated first if there
//     was an active control claim; otherwise left alone (cards in a
//     cemetery / hand belong to the pile they sit in).

import { revertAnimateAuraTransform } from '../automation/ops/cards/enchantressAura';

/**
 * Wipe every transient field on `card` so re-entering the realm is
 * effectively a fresh spawn. Mutates in place.
 */
export function resetCardOnLeavePlay(card) {
  if (!card) return;

  // An Enchantress-animated aura (type→Minion + cost-stat stamp) is a
  // transient transform like any buff. If the unit dies before its
  // timed identity-revert, that revert can't run (the card is no
  // longer a mesh), so undo it here. Otherwise the aura sits in the
  // cemetery as an unrecoverable minion. No-op for non-animated cards.
  revertAnimateAuraTransform(card);

  // Revert any active control claim BEFORE clearing. Restores the
  // original rotated flag so the card heads to its true owner's pile.
  if (card._controlClaimedBy && card._origRotated !== undefined) {
    card.rotated = card._origRotated;
  }

  // Status sets (manual + aura + suppressed keywords).
  card.statuses = [];
  card._auraStatuses = [];
  card._suppressedKeywords = [];

  // Ward grant tracking (printed-on-entry + per-aura).
  delete card._wardGranted;
  delete card._wardGrantedFromAuras;

  // Control claim tracking.
  delete card._controlClaimedBy;
  delete card._origRotated;

  // Aura stat delta (transient. Re-derived on next recompute).
  delete card._auraStatDelta;

  // Site "provides (E)" affinity stamps (Desert Bloom this-turn, Valley of
  // Delight permanent). A destroyed site provides nothing. This is the
  // permanent grant's source-alive check, and stops a stale stamp from riding
  // a replay of the card from a pile.
  delete card._providesThisTurn;
  delete card._providesPermanent;
  delete card._siteProvidesStamped;

  // Combat state. addCardToTable re-initialises currentAttack /
  // currentLife from baseAttack / baseLife on re-entry, so dropping
  // them here is enough to wipe damage and manual stat adjustments.
  card.currentAttack = undefined;
  card.currentLife = undefined;

  // Play-state flags. Direct write (not via GameBoard._applyTap)
  // because the card has just left the realm. Its mesh is being
  // destroyed and there is no animation, body, or broadcast to drive.
  // v1.37 unified-tap rule: in-play tap transitions go through
  // _applyTap; leave-play cleanup writes the bit directly.
  card.tapped = false;
  card.faceDown = false;
  // v1.30. Subsurface placement (burrowed/submerged). Cards always
  // re-enter the realm on the surface; the new entry's placement
  // prompt re-asks if the unit has the keyword.
  card.subsurface = null;

  // Interaction order is re-assigned on re-entry; group membership
  // is per-table and doesn't survive a leave.
  delete card.interactionOrder;
  delete card.groupId;

  // Equipment should already have been detached upstream of the
  // leave hook (removeCardFromTable / sendCardToPile both handle
  // detach). Defensively clear in case some path skipped that.
  delete card.equippedCards;

  // A deferred artifact Genesis that never resolved (card left play
  // while the Field/Equip choice was still open) is moot. Re-entry
  // is a fresh realm entry with its own Genesis.
  delete card._pendingFieldGenesis;

  // Server-side once-per-realm-entry Genesis stamp (an artifact's
  // processed on_play). A true re-entry opens a fresh Genesis.
  delete card._genesisFired;

  // First-time-at-cell tracking (Vaults of Zul / Wayfaring Pilgrim).
  // "First time" is scoped to this stay in the realm. A re-entered
  // card has visited nothing yet.
  delete card._firstVisitFired;
  delete card._firstVisitCells;

  // Skip-next-turn flag. Transient per stay in the realm.
  delete card._skipNextTurn;
  delete card._skipEndTriggers;
  delete card._untilLeavesStatuses;
  delete card._untilLeavesStatusBase;

  // Owner-locked draw (Omphalos / Morgana / Gabriel). The lock applies
  // to the one drawn copy in hand. Once it leaves the realm. Cast to
  // cemetery, or otherwise. The lock is spent and must not survive a
  // replay from the pile.
  delete card._castableOnlyBy;

  // Owner-seat stamp (Morgana FAQ owner-anchored return). Routing to the
  // owner's cemetery / banished pile resolves the destination BEFORE this
  // reset runs (findDestinationPile keys on `_ownerSeat` up front in
  // sendCardToPile), so clearing it here only prevents a stale owner
  // stamp from riding a replayed-from-pile copy.
  delete card._ownerSeat;
}
